// src/pages/inventory/Pharmacy/MedicineRequestModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
} from "@mui/material";
import { createMedicineRequest } from "../../../services/medicineRequestService";
import { useAuth } from "../../../context/AuthContext";
import { getRequestsByAdmissionId } from "../../../services/medicineRequestService";
import { getIpdPrescriptionsByAdmissionId } from "../../../services/ipdPrescriptionService";
import MedicationIcon from '@mui/icons-material/Medication';
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const MedicineRequestModal = ({ open, onClose, treatment, patientId, admissionId, onSubmit }) => {
  const { user } = useAuth();
  // Fetch duration and frequency from treatment or existing request - similar to how dosage is fetched
  const [duration, setDuration] = useState(treatment?.duration || "");
  const [frequency, setFrequency] = useState(treatment?.frequency || "");
  const [durationError, setDurationError] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  // Fetch frequency and duration from treatment, existing request, or IPD prescription
  useEffect(() => {
    const fetchFrequencyAndDuration = async () => {
      if (treatment && admissionId && treatment.id) {
        try {
          // Priority 1: Check if treatment object has frequency/duration (even though currently undefined)
          let foundFrequency = treatment.frequency || null;
          let foundDuration = treatment.duration || null;

          // Priority 2: Check existing medicine requests
          if (!foundFrequency || !foundDuration) {
            try {
              const requests = await getRequestsByAdmissionId(admissionId);
              const requestsArray = Array.isArray(requests) ? requests : (requests ? [requests] : []);
              
              const existingRequest = requestsArray.find(
                (req) => req.treatmentId === treatment.id || 
                         (req.medicineName === treatment.medicine && req.admissionId === parseInt(admissionId))
              );
              
              if (existingRequest) {
                if (!foundFrequency && existingRequest.frequency) {
                  foundFrequency = existingRequest.frequency;
                }
                if (!foundDuration && existingRequest.duration) {
                  foundDuration = existingRequest.duration.toString();
                }
              }
            } catch (reqError) {
              console.error("Error fetching existing requests:", reqError);
            }
          }

          // Priority 3: Fetch IPD prescription and match medicine to get frequency/duration
          // Since treatments are created from prescriptions with frequency/duration, they should be in the prescription
          if (!foundFrequency || !foundDuration) {
            try {
              const prescriptions = await getIpdPrescriptionsByAdmissionId(admissionId);
              
              // Debug: Log prescription response to see structure
              console.log("Prescription API Response:", prescriptions);
              
              // Handle different response structures
              let prescriptionsArray = [];
              if (Array.isArray(prescriptions)) {
                prescriptionsArray = prescriptions;
              } else if (prescriptions?.data) {
                prescriptionsArray = Array.isArray(prescriptions.data) ? prescriptions.data : [prescriptions.data];
              } else if (prescriptions) {
                prescriptionsArray = [prescriptions];
              }
              
              console.log("Prescriptions Array:", prescriptionsArray);
              
              if (prescriptionsArray.length > 0) {
                // Sort by creation date (most recent first)
                const sortedPrescriptions = [...prescriptionsArray].sort((a, b) => {
                  const dateA = a.createdAt ? (Array.isArray(a.createdAt) ? new Date(a.createdAt[0], a.createdAt[1] || 0, a.createdAt[2] || 0) : new Date(a.createdAt)) : new Date(0);
                  const dateB = b.createdAt ? (Array.isArray(b.createdAt) ? new Date(b.createdAt[0], b.createdAt[1] || 0, b.createdAt[2] || 0) : new Date(b.createdAt)) : new Date(0);
                  return dateB - dateA;
                });

                console.log("Looking for medicine:", treatment.medicine, "in prescriptions");

                // Check each prescription's treatmentRecords (which should contain the medicines with frequency/duration)
                for (const prescription of sortedPrescriptions) {
                  console.log("Checking prescription:", prescription.id, "treatmentRecords:", prescription.treatmentRecords, "medicines:", prescription.medicines);
                  
                  // Check treatmentRecords first (if backend returns them)
                  if (prescription.treatmentRecords && Array.isArray(prescription.treatmentRecords)) {
                    const matchingTreatment = prescription.treatmentRecords.find(
                      (tr) => tr.medicine === treatment.medicine || 
                              tr.medicineId === treatment.medicineId ||
                              tr.medicineName === treatment.medicine
                    );
                    
                    console.log("Matching treatment in treatmentRecords:", matchingTreatment);
                    
                    if (matchingTreatment) {
                      if (!foundFrequency && matchingTreatment.frequency) {
                        foundFrequency = matchingTreatment.frequency;
                        console.log("Found frequency from treatmentRecords:", foundFrequency);
                      }
                      if (!foundDuration && matchingTreatment.duration) {
                        foundDuration = matchingTreatment.duration.toString();
                        console.log("Found duration from treatmentRecords:", foundDuration);
                      }
                    }
                  }
                  
                  // Also check medicines array if it exists
                  if (prescription.medicines && Array.isArray(prescription.medicines)) {
                    const matchingMedicine = prescription.medicines.find(
                      (med) => med.name === treatment.medicine || 
                               med.medicineId === treatment.medicineId ||
                               med.medicineName === treatment.medicine
                    );
                    
                    console.log("Matching medicine in medicines array:", matchingMedicine);
                    
                    if (matchingMedicine) {
                      if (!foundFrequency && matchingMedicine.frequency) {
                        foundFrequency = matchingMedicine.frequency;
                        console.log("Found frequency from medicines array:", foundFrequency);
                      }
                      if (!foundDuration && matchingMedicine.duration) {
                        foundDuration = matchingMedicine.duration.toString();
                        console.log("Found duration from medicines array:", foundDuration);
                      }
                    }
                  }
                  
                  // If we found both, break early
                  if (foundFrequency && foundDuration) {
                    break;
                  }
                }
              }
            } catch (presError) {
              console.error("Error fetching prescription data:", presError);
            }
          }
          
          // Debug: Log what we found
          console.log("Final foundFrequency:", foundFrequency);
          console.log("Final foundDuration:", foundDuration);

          // Set the found values or leave empty for user to fill
          console.log("Setting frequency:", foundFrequency);
          console.log("Setting duration:", foundDuration);
          
          if (foundFrequency) {
            setFrequency(foundFrequency);
            console.log("✅ Frequency set to:", foundFrequency);
          } else {
            setFrequency("");
            console.log("⚠️ Frequency not found, leaving empty");
          }
          
          if (foundDuration) {
            setDuration(foundDuration);
            console.log("✅ Duration set to:", foundDuration);
          } else {
            setDuration("");
            console.log("⚠️ Duration not found, leaving empty");
          }
        } catch (error) {
          console.error("Error fetching frequency and duration:", error);
          // Make sure fields are reset on error
          setFrequency("");
          setDuration("");
        }
      } else {
        // Reset if no treatment
        setFrequency("");
        setDuration("");
      }
    };

    if (open && treatment) {
      // Reset to empty values when modal opens
      setDuration("");
      setFrequency("");
      setRemarks("");
      fetchFrequencyAndDuration();
    } else {
      // Reset when modal closes
      setDuration("");
      setFrequency("");
      setRemarks("");
    }
  }, [treatment, admissionId, open]);

  const calculateTotalDosage = (dosage) => {
    if (!dosage) return 0;
    return dosage.split('-').reduce((sum, num) => sum + parseInt(num.trim() || 0), 0);
  };

  const showAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
    setAlertMessage("");
  };

  const handleSubmitRequest = async () => {
    try {
      setLoading(true);
      
      // Validate duration
      if (!duration || Number(duration) < 1) {
        setDurationError("Duration must be at least 1 day");
        showAlert("Duration must be at least 1 day.", "error");
        return;
      }
      setDurationError("");

      // Validate frequency (optional but recommended)
      if (!frequency || frequency.trim() === "") {
        // Allow submission without frequency but warn user
        // You can make this required by uncommenting the return statement below
        // setDurationError("Frequency is required");
        // showAlert("Frequency is required.", "error");
        // return;
      }

      if (!treatment?.medicine) {
        showAlert("No medicine found in treatment.", "error");
        return;
      }

      // Calculate total quantity
      const totalQuantity = calculateTotalDosage(treatment.dosage) * Number(duration);
      
      if (totalQuantity <= 0) {
        showAlert("Total quantity must be greater than 0.", "error");
        return;
      }

      // Get frequency and duration from user input (since treatment object doesn't have these fields)
      // These are entered by the user in the form fields
      const frequencyFromPrescription = frequency || "";
      const durationFromPrescription = duration || "";

      // Prepare medicine request data
      const requestData = {
        patientId: parseInt(patientId) || 0,
        treatmentId: parseInt(treatment.id) || null,
        admissionId: parseInt(admissionId) || null,
        medicineId: parseInt(treatment.medicineId) || 0,
        medicineName: treatment.medicine || "",
        dosage: treatment.dosage || "", // From treatment object
        frequency: frequencyFromPrescription || "As prescribed", // From user input
        duration: parseInt(durationFromPrescription) || 0, // From user input
        totalQuantity: totalQuantity,
        requestedByUserId: parseInt(user?.id) || 0,
        requestedByName: user?.name || user?.username || "Unknown",
        requestedDate: new Date().toISOString(),
        status: "REQUESTED",
        remarks: remarks || null,
        // Additional treatment info
        treatmentType: treatment.treatmentType || "",
        performedByName: treatment.performedByName || "",
      };

      // Create the request
      const createdRequest = await createMedicineRequest(requestData);
      
      showAlert("Medicine request submitted successfully! Waiting for pharmacy approval.", "success");
      
      // Wait to show alert, then close
      setTimeout(() => {
        if (onSubmit) {
          onSubmit(createdRequest);
        }
        onClose();
        setDuration("");
        setFrequency("");
        setRemarks("");
      }, 2000);
      
    } catch (error) {
      console.error("Error submitting medicine request:", error);
      
      let errorMessage = "Failed to submit medicine request";
      if (error.response) {
        errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage = "Network error: No response from server";
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      showAlert(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!treatment) {
    return null;
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
          Request Medicine from Pharmacy
        </DialogTitle>
        
        <DialogContent>
          {/* Info Alert */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Request Process</AlertTitle>
            Your medicine request will be sent to the pharmacy admin for approval. Once approved, the pharmacy will issue the medicine for you to collect.
          </Alert>

          {/* Treatment Information */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MedicationIcon color="primary" />
              Treatment Details
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip label={`Type: ${treatment.treatmentType}`} color="primary" variant="outlined" />
              <Chip label={`By: ${treatment.performedByName}`} color="secondary" variant="outlined" />
              {treatment.treatmentDate && (
                <Chip label={`Date: ${new Date(treatment.treatmentDate).toLocaleDateString()}`} color="default" variant="outlined" />
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
          </Box>

          {/* Medicine Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'primary.main' }}>
              {treatment.medicine}
            </Typography>
            
            <Box display="flex" gap={2} sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Dosage"
                value={treatment.dosage}
                disabled
                fullWidth
                margin="normal"
                sx={{ maxWidth: '150px' }}
                InputProps={{
                  startAdornment: <MedicationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              
              <TextField
                label="Frequency"
                value={frequency || "As prescribed"}
                disabled
                fullWidth
                margin="normal"
                sx={{ maxWidth: '150px' }}
                // helperText={frequency ? "From prescription" : "Default: As prescribed"}
              />
              
              <TextField
                label="Duration (days)"
                type="number"
                value={duration || ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? "" : parseInt(e.target.value);
                  if (e.target.value === "") {
                    setDuration("");
                    setDurationError("");
                    return;
                  }
                  if (isNaN(value) || value < 1) {
                    setDurationError("Duration must be at least 1 day");
                    return;
                  }
                  setDuration(value.toString());
                  setDurationError("");
                }}
                error={!!durationError}
                helperText={durationError}
                fullWidth
                margin="normal"
                inputProps={{ min: 1 }}
                sx={{ maxWidth: '150px' }}
                required
              />
            </Box>
            
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 2 }}>
              Total Quantity Requested: <strong>{calculateTotalDosage(treatment.dosage) * (Math.max(0, Number(duration || 0)))} units</strong>
            </Typography>
            
            {treatment.remarks && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Treatment Remarks:</strong> {treatment.remarks}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Additional Remarks for Request */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Request Remarks (Optional)"
              multiline
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any special instructions or notes for pharmacy..."
              disabled={loading}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={onClose} color="primary" variant="outlined" sx={FORM_CANCEL_BUTTON_SX} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitRequest} 
            color="primary" 
            variant="contained"
            sx={FORM_SUBMIT_BUTTON_SX}
            disabled={!treatment.medicine || loading || !duration || Number(duration) < 1}
          >
            {loading ? <CircularProgress size={20} /> : "Submit Request to Pharmacy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <Dialog 
        open={alertOpen} 
        onClose={handleCloseAlert}
        maxWidth="md" 
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <Alert 
          severity={alertType} 
          onClose={handleCloseAlert}
          sx={{ 
            fontSize: '1.1rem',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <AlertTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
            {alertType === "success" ? "Success!" : "Error!"}
          </AlertTitle>
          {alertMessage}
        </Alert>
      </Dialog>
    </>
  );
};

export default MedicineRequestModal;

